import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const storageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return '/storage/' + path.replace(/^\/+/, '');
};

const PublicOrderTickets = () => {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios.get(`${API_BASE}/orders/public/${token}`)
            .then(res => {
                if (res.data.success) {
                    setData(res.data.order);
                } else {
                    setError('Tickets not found.');
                }
            })
            .catch(() => setError('Could not load tickets. The link may be invalid or expired.'))
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) {
        return (
            <div style={styles.center}>
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-3 text-muted">Loading your tickets…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.center}>
                <i className="fas fa-exclamation-circle fa-3x text-danger mb-3" />
                <p className="text-danger font-weight-bold">{error}</p>
            </div>
        );
    }

    const event        = data.event;
    const primaryImage = event.images?.find(i => i.is_primary) || event.images?.[0];
    const eventDate    = event.start_datetime
        ? new Date(event.start_datetime).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        : null;

    return (
        <div style={styles.page}>
            {/* Top bar with logo */}
            <div style={styles.topBar}>
                <img src="/ticketaty-logo.svg" alt="Ticketaty" style={styles.topLogo} />
            </div>

            {/* Header */}
            <div style={styles.header}>
                {primaryImage && (
                    <img
                        src={storageUrl(primaryImage.image_path)}
                        alt={event.name}
                        style={styles.eventBanner}
                        onError={e => { e.target.style.display = 'none'; }}
                    />
                )}
                <div style={styles.headerOverlay}>
                    <h1 style={styles.eventName}>{event.name}</h1>
                    {eventDate && <p style={styles.eventMeta}><i className="fas fa-calendar-alt mr-2" />{eventDate}</p>}
                    {event.location && <p style={styles.eventMeta}><i className="fas fa-map-marker-alt mr-2" />{event.location}</p>}
                </div>
            </div>

            {/* Order summary */}
            <div style={styles.summaryBar}>
                <span>Order <strong>#{data.id}</strong></span>
                <span>{data.tickets.length} ticket{data.tickets.length !== 1 ? 's' : ''}</span>
                <span><strong>{Number(data.total_amount).toFixed(2)} {event.currency || 'QAR'}</strong></span>
            </div>

            {/* Tickets */}
            <div style={styles.ticketGrid}>
                {data.tickets.map((ticket, idx) => {
                    const categoryName = ticket.ticket_category?.category_name || 'Ticket';
                    const isValet      = ticket.ticket_category?.type === 'valet';
                    const seatLabel    = ticket.seat ? `${ticket.seat.row_label || ''}${ticket.seat.label}`.trim() : null;
                    const sectionName  = ticket.section?.name || null;

                    return (
                        <div key={ticket.id} style={styles.ticketCard}>
                            <div style={styles.ticketLeft}>
                                <span style={{ ...styles.ticketBadge, background: isValet ? '#f39c12' : '#2980b9' }}>
                                    {isValet ? <><i className="fas fa-car mr-1" />Valet</> : <><i className="fas fa-ticket-alt mr-1" />Ticket</>}
                                </span>
                                <h3 style={styles.ticketCategory}>{categoryName}</h3>
                                {sectionName && <p style={styles.ticketDetail}><i className="fas fa-map-signs mr-1" />{sectionName}</p>}
                                {seatLabel   && <p style={styles.ticketDetail}><i className="fas fa-chair mr-1" />Seat {seatLabel}</p>}
                                <p style={styles.ticketDetail}>#{String(idx + 1).padStart(2, '0')} of {data.tickets.length}</p>
                            </div>
                            <div style={styles.ticketRight}>
                                <div style={styles.qrWrap}>
                                    <QRCode value={ticket.ticket_qr_code} size={140} />
                                </div>
                                <p style={styles.qrCode}>{ticket.ticket_qr_code}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div style={styles.footer}>
                <p style={styles.footerText}>Present each QR code at the venue entrance for scanning.</p>
                <img src="/ticketaty-logo.svg" alt="Ticketaty" style={styles.footerLogo} />
            </div>
        </div>
    );
};

const styles = {
    page: {
        minHeight: '100vh',
        background: '#f4f6f8',
        fontFamily: "'Segoe UI', sans-serif",
        paddingBottom: 40,
    },
    topBar: {
        background: '#fff',
        padding: '10px 20px',
        borderBottom: '1px solid #e0e0e0',
    },
    topLogo: {
        height: 32,
        display: 'block',
    },
    center: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
    },
    header: {
        position: 'relative',
        minHeight: 180,
        background: '#1a1a2e',
        overflow: 'hidden',
    },
    eventBanner: {
        width: '100%',
        maxHeight: 280,
        objectFit: 'cover',
        opacity: 0.45,
        display: 'block',
    },
    headerOverlay: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '24px 24px 20px',
        background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
    },
    eventName: {
        color: '#fff',
        fontSize: 26,
        fontWeight: 700,
        margin: '0 0 6px',
    },
    eventMeta: {
        color: 'rgba(255,255,255,0.85)',
        fontSize: 14,
        margin: '2px 0',
    },
    summaryBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#fff',
        padding: '12px 24px',
        fontSize: 14,
        color: '#555',
        borderBottom: '1px solid #e0e0e0',
        flexWrap: 'wrap',
        gap: 8,
    },
    ticketGrid: {
        maxWidth: 680,
        margin: '24px auto 0',
        padding: '0 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    ticketCard: {
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        display: 'flex',
        overflow: 'hidden',
    },
    ticketLeft: {
        flex: 1,
        padding: '20px 20px 20px 24px',
        borderRight: '2px dashed #e0e0e0',
    },
    ticketRight: {
        padding: '20px 20px 20px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 180,
    },
    ticketBadge: {
        display: 'inline-block',
        color: '#fff',
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 10px',
        borderRadius: 20,
        marginBottom: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    ticketCategory: {
        fontSize: 20,
        fontWeight: 700,
        color: '#1a1a2e',
        margin: '0 0 8px',
    },
    ticketDetail: {
        fontSize: 13,
        color: '#666',
        margin: '3px 0',
    },
    qrWrap: {
        background: '#fff',
        padding: 8,
        borderRadius: 8,
        border: '1px solid #eee',
    },
    qrCode: {
        fontSize: 10,
        color: '#999',
        marginTop: 8,
        letterSpacing: 1,
        textAlign: 'center',
        wordBreak: 'break-all',
    },
    footer: {
        textAlign: 'center',
        marginTop: 32,
        padding: '0 16px 8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
    },
    footerText: {
        color: '#999',
        fontSize: 13,
        margin: 0,
    },
    footerLogo: {
        height: 28,
        opacity: 0.6,
    },
};

export default PublicOrderTickets;
